/**
 * Chip — filter / selector chip primitive.
 *
 * Figma source: `chips` component-set `5:175`. Inspected variants:
 *  - state=default  (5:168): transparent fill + border surface-4 @ α=0.16, text-secondary
 *  - state=selected (5:173): bg-glass-16 fill + no border, text-primary
 *
 * Shared geometry (both): H=30 (HUG), px-4, py-2, itemSpacing 6,
 * cornerRadius 16. Text: Raleway SemiBold 12/14.088.
 *
 * Optional sub-frames in selected variant (PREMIUM label, counter sub-chip)
 * exist in Figma but are gated to invisible variants — not implemented in
 * this primitive. If a use case arises, add a `counter` / `premium` prop
 * and re-inspect the specific Figma variant first (manifest §9 gap).
 *
 * Props:
 *  - selected?  (bool)      — default false
 *  - onClick?   (fn)
 *  - leftIcon?  (ReactNode) — optional element rendered before children (e.g. <img> for payment icons)
 *  - children   (label)
 *  - ...rest    → <button>
 *
 * Extension note (stage 08.4): `leftIcon` added for PaymentMethodPicker chips
 * that carry payment-method brand icons from /public/icons/payment/.
 * These are multi-color SVGs (pattern fills) that must be rendered via <img>,
 * not via CSS mask-image — so we accept ReactNode instead of an icon name string.
 */
export default function Chip({
  selected = false,
  onClick,
  leftIcon,
  children,
  className = '',
  ...rest
}) {
  const stateCls = selected
    ? 'bg-glass-16 text-text-primary'
    : 'border border-border-default text-text-secondary';

  const cls = [
    // Figma shared auto-layout: HORIZONTAL, center/center, gap 6
    'inline-flex items-center justify-center gap-1.5 flex-shrink-0',
    // Figma sizing: H=30, pad L/R=16 T/B=8
    'h-[30px] px-4 py-2',
    // cornerRadius 16
    'rounded-home-16',
    // Typography — Raleway SemiBold 12/14.088 (from fontSize token chip-label)
    'font-sans text-chip-label',
    stateCls,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cls}
      {...rest}
    >
      {leftIcon && leftIcon}
      {children}
    </button>
  );
}
